"use client";

import { Consent, useConsent } from "@packages/consent";
import { useSearch } from "@packages/framework";
import { matchAppstack, useSession } from "@packages/sdk";
import { useEffect, useRef } from "react";

import type { AnalyticsEvent } from "../types/analyticsEvent.types";
import { HallowAnalyticsEvent } from "../types/enums/hallowAnalyticsEvent.enum";

/**
 * Appstack click↔install matching for web signups.
 *
 * The web "install" moment is account creation (Appstack: use our user_id as
 * appstack_id). When a newly-created user is authenticated in this browser,
 * fire the Appstack deeplink match request so the device that clicked the ad
 * link is associated with the user_id that pipe's AppstackDestination sends
 * on all S2S events (INSTALL / REGISTER / START_TRIAL / SUBSCRIBE).
 *
 * Guards:
 * - advertising consent required (same gate as GTM/ad pixels)
 * - skipped inside native webviews (mobile journeys are not web installs)
 * - once per user per browser (localStorage, handled by matchAppstack)
 * - no-op when Appstack env vars are unset
 */
export const useAppstackInstallMatch = ({
  track,
}: {
  track: (event: AnalyticsEvent) => void | Promise<void>;
}) => {
  const search = useSearch();
  const { consent } = useConsent();
  const { jwtPayload, status } = useSession();

  /** track is recreated every provider render; a ref keeps it out of the effect deps. */
  const trackRef = useRef(track);
  trackRef.current = track;

  const isTrackCrossPlatform = search.get("isTrackCrossPlatform") === "true";
  const isNewUser = search.get("isNewUser") === "true";
  const hasAdvertisingConsent =
    Array.isArray(consent) && consent.includes(Consent.ADVERTISING);
  const userId = jwtPayload?.id;

  useEffect(() => {
    if (
      status !== "authenticated" ||
      !isNewUser ||
      !hasAdvertisingConsent ||
      isTrackCrossPlatform ||
      !userId
    )
      return;

    void matchAppstack({
      onError: (error) =>
        trackRef.current({
          event: HallowAnalyticsEvent.AppstackMatchFailed,
          properties: {
            error: error instanceof Error ? error.message : String(error),
          },
        }),
      userId,
    });
  }, [status, isNewUser, hasAdvertisingConsent, isTrackCrossPlatform, userId]);
};
